import { useEffect, useState } from "react";
import { Database, ExternalLink } from "lucide-react";
import client from "@/lib/api";

const val = (v) => (v == null || v === "" ? "—" : typeof v === "boolean" ? (v ? "yes" : "no") : String(v).slice(0, 40));

export const RelatedForms = ({ query }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!query) return;
    client.get("/ezap/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    client.get(`/ezap/forms/related?q=${encodeURIComponent(query)}`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [query]);
  if (!status) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="related-forms-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Database className="h-3.5 w-3.5" /> Related forms · source: Entrance Zone Admin Panel / Form Listing · read-only</p>
        <a href="https://ezap.entrancezone.com/form-listing" target="_blank" rel="noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1" data-testid="related-forms-panel-link">open panel <ExternalLink className="h-3 w-3" /></a>
      </div>
      {!status.connected && <p className="text-sm text-slate-500" data-testid="related-forms-not-connected">Admin Panel not connected — connect it under Settings to reference form-listing records here.</p>}
      {status.connected && data && data.items.length === 0 && <p className="text-sm text-slate-500" data-testid="related-forms-empty">No form-listing records match this topic ({status.cached_forms} cached, last read {status.last_sync_at ? new Date(status.last_sync_at).toLocaleString("en-IN") : "never"}).</p>}
      {data && data.items.length > 0 && (
        <table className="w-full text-xs" data-testid="related-forms-table">
          <thead><tr className="text-left text-slate-500"><th className="py-1">Form</th><th className="py-1">Short name</th><th className="py-1">Status</th><th className="py-1">Dates</th><th className="py-1">Match</th></tr></thead>
          <tbody>
            {data.items.map((f) => (
              <tr key={f.form_id} className="border-t" data-testid={`related-form-${f.form_id}`}>
                <td className="py-1.5 font-medium">{f.name}</td><td className="py-1.5 font-mono">{val(f.short_name)}</td>
                <td className="py-1.5">{val(f.fields?.status ?? f.fields?.isActive ?? f.fields?.active ?? f.fields?.published)}</td>
                <td className="py-1.5 text-slate-500">{val(f.fields?.startDate || f.fields?.applicationStart)} → {val(f.fields?.endDate || f.fields?.lastDate || f.fields?.applicationEnd)}</td>
                <td className="py-1.5">{f.match_score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const EzapConnectCard = () => {
  const [st, setSt] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => client.get("/ezap/status").then((r) => setSt(r.data)).catch(() => setSt(null));
  useEffect(() => { load(); }, []);
  const connect = async () => {
    setBusy(true); setMsg("");
    try { await client.post("/ezap/connect", { email, password }); setPassword(""); setMsg("Connected (read-only). Credentials stored encrypted."); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await client.post("/ezap/sync");
      if (r.data.already_running) { setMsg("A read is already running — see progress below."); }
      else { setMsg("Read started in the background (833 forms × /basic takes a few minutes)."); }
      const poll = setInterval(async () => { const s = await client.get("/ezap/status"); setSt(s.data); if (!s.data.syncing) { clearInterval(poll); setMsg(`Read ${s.data.last_sync_count} forms (panel reports ${s.data.sync_total ?? s.data.last_sync_count}).`); setBusy(false); } else { setMsg(`Reading… ${s.data.sync_progress ?? 0} / ${s.data.sync_total ?? "?"} forms`); } }, 3000);
    }
    catch (e) { setMsg(e?.response?.data?.detail || "Read failed"); setBusy(false); }
  };
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="ezap-connect-card">
      <div>
        <h3 className="font-semibold">Entrance Zone Admin Panel — Form Listing (read-only)</h3>
        <p className="text-xs text-muted-foreground">GET-only session against ezap.entrancezone.com. Credentials are encrypted in the database, never logged or shown. Every read is audit-logged. No WordPress or Brain actions are connected to this data.</p>
      </div>
      {st && <div className="text-xs flex flex-wrap gap-2" data-testid="ezap-status">
        <span className={`rounded-full border px-2 py-0.5 ${st.connected ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-secondary"}`}>{st.connected ? `connected · ${st.account}` : "not connected"}</span>
        <span className="rounded-full border px-2 py-0.5">cached forms: {st.cached_forms}</span>
        {st.last_sync_at && <span className="rounded-full border px-2 py-0.5">last read {new Date(st.last_sync_at).toLocaleString("en-IN")}</span>}
        {st.last_error && <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-rose-700">{st.last_error}</span>}
      </div>}
      <div className="flex flex-wrap gap-2">
        <input data-testid="ezap-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="panel email" className="rounded-md border bg-background px-2 py-1.5 text-sm" />
        <input data-testid="ezap-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="panel password" className="rounded-md border bg-background px-2 py-1.5 text-sm" />
        <button data-testid="ezap-connect-btn" disabled={busy || !email || !password} onClick={connect} className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">Connect (read-only)</button>
        <button data-testid="ezap-sync-btn" disabled={busy || !st?.connected} onClick={sync} className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50">Read form listing now</button>
      </div>
      {msg && <p className="text-xs" data-testid="ezap-msg">{msg}</p>}
    </div>
  );
};
